import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCreateTenantMutation } from '../../slices/tenantsApiSlice';
import { useGetHousesQuery } from '../../slices/housesApiSlice';
import { useGetLandlordsQuery } from '../../slices/landlordsApiSlice';

const AddStaffTenant = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    house: '',
    address: '',
    type: '',
    monthlyRent: '',
    securityDeposit: '',
    description: '',
    landlord: '',
    startDate: '',
    status: 'Active',
    contact: '',
    images: [],
  });

  const [houseFilter, setHouseFilter] = useState('');
  const [landlordFilter, setLandlordFilter] = useState('');

  const [createTenant, { isLoading, error }] = useCreateTenantMutation();
  const { data: housesData } = useGetHousesQuery({ keyword: houseFilter });
  const { data: landlordsData } = useGetLandlordsQuery({ keyword: landlordFilter });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024;
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.name}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File too large: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const handleFileUpload = async (files) => {
    if (!validateFiles(files)) return;

    const uploadData = new FormData();
    Array.from(files).forEach((file) => uploadData.append('images', file));

    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        body: uploadData,
      });
      const data = await res.json();
      if (res.ok) {
        setFormData((prev) => ({
          ...prev,
          images: [
            ...prev.images,
            ...data.images.map((img) => (typeof img === 'string' ? img : img.path || '')),
          ].filter(Boolean),
        }));
        toast.success('Images uploaded');
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await createTenant(formData).unwrap();
      toast.success('Tenant added successfully');
      navigate('/tenantlistscreen');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to add tenant');
    }
  };

  const filteredHouses = housesData?.houses || [];
  const filteredLandlords = landlordsData?.landlords || [];

  return (
    <div className="container mt-4">
      <h1>Add New Tenant</h1>
      <Form onSubmit={submitHandler}>
        <Form.Group className="mb-3" controlId="name">
          <Form.Label>Tenant Name</Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="house">
          <Form.Label>House</Form.Label>
          <Form.Control
            type="text"
            placeholder="Filter houses..."
            className="mb-2"
            value={houseFilter}
            onChange={(e) => setHouseFilter(e.target.value)}
          />
          <Form.Control
            as="select"
            name="house"
            value={formData.house}
            onChange={handleChange}
            required
          >
            <option value="">Select House</option>
            {filteredHouses.map((h) => (
              <option key={h._id} value={h._id}>
                {h.name} - {h.address}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-3" controlId="landlord">
          <Form.Label>Landlord</Form.Label>
          <Form.Control
            type="text"
            placeholder="Filter landlords..."
            className="mb-2"
            value={landlordFilter}
            onChange={(e) => setLandlordFilter(e.target.value)}
          />
          <Form.Control
            as="select"
            name="landlord"
            value={formData.landlord}
            onChange={handleChange}
            required
          >
            <option value="">Select Landlord</option>
            {filteredLandlords.map((l) => (
              <option key={l._id} value={l._id}>
                {l.name} - {l.company}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-3" controlId="address">
          <Form.Label>Tenant Address</Form.Label>
          <Form.Control
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="type">
          <Form.Label>Unit Type</Form.Label>
          <Form.Control
            as="select"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
          >
            <option value="">Select Type</option>
            <option value="Apartment">Apartment</option>
            <option value="Shop">Single Room</option>
            <option value="Single Room">Single Room</option>
            <option value="Studio">Studio</option>
            <option value="Shared">Shared</option>
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-3" controlId="monthlyRent">
          <Form.Label>Monthly Rent</Form.Label>
          <Form.Control
            type="number"
            name="monthlyRent"
            value={formData.monthlyRent}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="securityDeposit">
          <Form.Label>Security Deposit</Form.Label>
          <Form.Control
            type="number"
            name="securityDeposit"
            value={formData.securityDeposit}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="startDate">
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="status">
          <Form.Label>Status</Form.Label>
          <Form.Control
            as="select"
            name="status"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Evicted">Evicted</option>
            <option value="Pending">Pending</option>
          </Form.Control>
        </Form.Group>

        <Form.Group className="mb-3" controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="contact">
          <Form.Label>Tenant Contact</Form.Label>
          <Form.Control
            type="text"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            required
          />
        </Form.Group>


        <Form.Group className="mb-3" controlId="images">
          <Form.Label>Upload Contract Images</Form.Label>
          <Form.Control
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
          <div className="mt-3">
            {formData.images.map((img, idx) => (
              <div
                key={idx}
                style={{
                  display: 'inline-block',
                  position: 'relative',
                  marginRight: '10px',
                }}
              >
                <img
                  src={img}
                  alt={`Uploaded ${idx}`}
                  className="img-fluid"
                  style={{
                    maxWidth: '100px',
                    height: '100px',
                    objectFit: 'cover',
                  }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeImage(idx)}
                  style={{
                    position: 'absolute',
                    top: '0',
                  
                    right: '0',
                    borderRadius: '50%',
                    padding: '2px 5px',
                  }}
                >
                  ✖
                </Button>
              </div>
            ))}
          </div>
        </Form.Group>

        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Adding Tenant...' : 'Add Tenant'}
        </Button>

        {error && (
          <p className="text-danger mt-2">
            {error?.data?.message || 'Error adding tenant'}
          </p>
        )}
      </Form>
    </div>
  );
};

export default AddStaffTenant;